import React, { useState } from 'react'
import { Button, Layout } from 'antd'
import styled from 'styled-components'
import { MapMunicipalitySearchForm } from './map-municipality-search/MapMunicipalitySearchForm'
import { GeneralContainer } from './GeneralContainer'
import { MapMetricSearch } from './MapMetricSearch'
import { MapAutonomousCommunity, MapMunicipality, MapMunicipalityWitGeojson, MapProvince } from '../../services/map-municipalities.service'
import { MapMetric } from '../../services/map-metrics.service'
import { SPAIN } from '../../pages/MapPage'

const { Content } = Layout

type Props = {
  setMapContent: (params: { metric: MapMetric, municipalities: MapMunicipality[], provinces: MapProvince[], autonomousCommunities: MapAutonomousCommunity[] }) => void
  metric: MapMetric,
  autonomousCommunities: MapAutonomousCommunity[],
}

export const MapContainer: React.FC<Props> = ({
  setMapContent,
  metric,
  autonomousCommunities,
  children }) => {

  const [selectedMetric, setSelectedMetric] = useState<MapMetric>(metric);

  const [selectedMunicipalities, setSelectedMunicipalities] = useState<MapMunicipalityWitGeojson[]>([])
  const [selectedProvinces, setSelectedProvinces] = useState<MapProvince[]>([])
  const [selectedAutonomousCommunities, setSelectedAutonomousCommunities] = useState<MapAutonomousCommunity[]>(autonomousCommunities)
  
  const [formKey, setFormKey] = useState(0)

  const handleMunicipalitySelection = (municipality: MapMunicipalityWitGeojson) => {
    setFormKey(prev => prev + 1)
    if (selectedProvinces.some(province => province.id === municipality.province!.id)
      || selectedAutonomousCommunities.some(autonomousCommunity => autonomousCommunity.id === municipality.autonomousCommunity.id)) {
      return;
    }
    setSelectedMunicipalities([
      ...selectedMunicipalities.filter(it => it.id !== municipality.id),
      municipality
    ])
  }

  const handleProvinceSelection = (province: MapProvince) => {
    setFormKey(prev => prev + 1)
    if (selectedAutonomousCommunities.some(autonomousCommunity => autonomousCommunity.id === province.autonomousCommunity.id)) {
      return;
    }
    setSelectedProvinces([
      ...selectedProvinces.filter(it => it.id !== province.id),
      province
    ])
    setSelectedMunicipalities([
      ...selectedMunicipalities.filter(it => it.province.id !== province.id)
    ])
  }

  const handleAutonomousCommunitySelection = (autonomousCommunity: MapAutonomousCommunity) => {
    setFormKey(prev => prev + 1)
    setSelectedAutonomousCommunities([
      ...selectedAutonomousCommunities.filter(it => it.id !== autonomousCommunity.id),
      autonomousCommunity
    ])
    setSelectedProvinces([
      ...selectedProvinces.filter(it => it.autonomousCommunity.id !== autonomousCommunity.id)
    ])
    setSelectedMunicipalities([
      ...selectedMunicipalities.filter(it => it.autonomousCommunity.id !== autonomousCommunity.id)
    ])
  }

  const handleSelectWholeSpain = () => {
    setFormKey(prev => prev + 1)
    setSelectedAutonomousCommunities([SPAIN]);
    setSelectedProvinces([])
    setSelectedMunicipalities([])
  }

  const handleMunicipalityRemoval = (municipality: MapMunicipality) => {
    setSelectedMunicipalities([...selectedMunicipalities.filter(it => it.id !== municipality.id)]);
  }

  const handleProvinceRemoval = (province: MapProvince) => {
    setSelectedProvinces([...selectedProvinces.filter(it => it.id !== province.id)]);
  }

  const handleAutonomousCommunityRemoval = (autonomousCommunity: MapAutonomousCommunity) => {
    setSelectedAutonomousCommunities([...selectedAutonomousCommunities.filter(it => it.id !== autonomousCommunity.id)]);
  }

  const sendSelectionToMap = () => {
    setMapContent({
      metric: selectedMetric!,
      municipalities: selectedMunicipalities,
      provinces: selectedProvinces,
      autonomousCommunities: selectedAutonomousCommunities
    })
  }

  const renderSelectedMunicipalities = () => (<>
    {
      selectedAutonomousCommunities.map(autonomousCommunity =>
        <Tag key={autonomousCommunity.id} onClick={(e) => handleAutonomousCommunityRemoval(autonomousCommunity)} >{autonomousCommunity.name} ✕</Tag>
      )
    }
    {
      selectedProvinces.map(province =>
        <Tag key={province.id} onClick={(e) => handleProvinceRemoval(province)} >{province.name} ✕</Tag>
      )
    }
    {
      selectedMunicipalities.map(municipality =>
        <Tag key={municipality.id} onClick={(e) => handleMunicipalityRemoval(municipality)} >{municipality.name} ✕</Tag>
      )
    }
  </>)

  const isButtonEnabled = selectedMetric && (selectedMunicipalities.length || selectedProvinces.length || selectedAutonomousCommunities.length)

  return (
    <BaseLayout>
      <GeneralContainer>
        <div className="db-search-card">
          <MapMetricSearch handleMetricSelection={setSelectedMetric} selectedMetric={selectedMetric} />
          <MapMunicipalitySearchForm
            key={formKey}
            handleSelectWholeSpain={handleSelectWholeSpain}
            handleAutonomousCommunitySelection={handleAutonomousCommunitySelection}
            handleProvinceSelection={handleProvinceSelection}
            handleMunicipalitySelection={handleMunicipalitySelection}

            selectedMunicipalities={selectedMunicipalities}
            selectedProvinces={selectedProvinces}
            selectedAutonomousCommunities={selectedAutonomousCommunities}
          />

          <Row>
            <Col9>
              <MunicipContainer>{renderSelectedMunicipalities()}</MunicipContainer>
            </Col9>
            <Col3>
              <StyledButton onClick={() => sendSelectionToMap()} disabled={!isButtonEnabled}>Mostrar</StyledButton>
            </Col3>
          </Row>
        </div>
      </GeneralContainer>

      <Layout>
        <Content style={{ backgroundColor: 'white', position: 'relative' }}>
          {children}
        </Content>
      </Layout>
    </BaseLayout>
  )
}

const BaseLayout = styled(Layout)`
  min-height: 100vh;
  background-color: white;
`

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 15px;
`;


const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
`;

const Col3 = styled.div`
  flex: 0 0 25%;
  max-width: 25%;
  padding: 0 15px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    max-width: 100%;
    padding-bottom: 10px;
  }
`;

const Tag = styled.div`
  display: inline-block;
  background-color: #e0e0e0;
  border-radius: 15px;
  padding: 2px 5px;
  margin: 15px 5px 0 0;
  font-size: 10px;
  cursor: pointer;
  user-select: none;
  color: #333;
`;

const MunicipContainer = styled.div`
  margin-bottom: 10px;
`;

const Col9 = styled.div`
  flex: 0 0 75%;
  max-width: 75%;
  padding: 0 15px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    flex: 0 0 100%;
    max-width: 100%;
    padding-bottom: 10px;
  }
`;